import {
  MIN_RIGHT_SIDEBAR_WIDTH,
  DEFAULT_RIGHT_SIDEBAR_WIDTH,
  RIGHT_SIDEBAR_NARROW_OPEN_OVERRIDE_KEY,
  RIGHT_SIDEBAR_OPEN_PREFERENCE_KEY,
  RIGHT_SIDEBAR_WIDTH_KEY,
} from "../agent-session/hooks/useRightSidebar";
import {
  canFitLeftSidebar,
  canFitRightSidebar,
  estimateAppNavRailWidth,
} from "./usePanelSpaceBudget";
import { clampSidebarWidth, sidebarWidthConfig } from "./useResizableSidebar";
import {
  readCachedSidebarVariant,
  type CodesmithSidebarVariant,
} from "./sidebarVariant";
import { SESSION_SIDEBAR_COLLAPSED_KEY } from "./useSidebarCollapsedState";
import { readCachedWorkspacePaneEnabled } from "../agent-session/workspace-pane/workspacePaneFlag";
import {
  workspacePaneIdFromQuery,
  WORKSPACE_PANE_QUERY_PARAM,
} from "../agent-session/workspace-pane/useWorkspacePaneUrl";
import { MIN_WORKSPACE_PANE_WIDTH } from "../agent-session/workspace-pane/dimensions";

export { useIsHydrated } from "./useIsHydrated";

/* ---------------------------------------------------------------------------
 * The session skeletons must occupy the same footprint the real panels will
 * restore from localStorage, or their mount shifts the whole layout: the
 * left session sidebar keeps a user-resized width (and may be collapsed),
 * and the right details panel keeps both an open preference and a width.
 *
 * The helpers here read those persisted values. They touch `window`, so
 * callers gate them behind `useIsHydrated()` — during SSR and hydration the
 * skeletons render the same defaults the server produced.
 * -------------------------------------------------------------------------*/

function readStoredValue<T extends number | boolean>(
  storage: Storage,
  key: string,
  defaultValue: T,
): T {
  try {
    const raw = storage.getItem(key);
    if (raw === null) return defaultValue;
    const parsed: unknown = JSON.parse(raw);
    return typeof parsed === typeof defaultValue ? (parsed as T) : defaultValue;
  } catch {
    return defaultValue;
  }
}

/** Read a JSON value persisted by usehooks-ts `useLocalStorage`. */
export function readStoredLayoutValue<T extends number | boolean>(
  key: string,
  defaultValue: T,
): T {
  return readStoredValue(window.localStorage, key, defaultValue);
}

/**
 * The same pre-measure estimate `useCodesmithContainerWidth` falls back to;
 * the skeleton never has the container element to measure.
 */
function estimateContainerWidth(): number {
  return window.innerWidth - estimateAppNavRailWidth();
}

export interface LeftSidebarSkeletonLayout {
  visible: boolean;
  width: number;
  variant: CodesmithSidebarVariant;
}

/**
 * The session skeletons only render on `/[org]/...` routes, so the org is the
 * first path segment; `/acme/sessions/new` → `"acme"`.
 */
export function orgSlugFromPath(path: string): string | null {
  return path.split(/[?#]/)[0].split("/").filter(Boolean)[0] ?? null;
}

// Reads the URL org's cached flag (like the mounted sidebar does once the
// route is parsed), so switching orgs lands on that org's variant instead
// of the last-resolved org's.
export function readSkeletonSidebarVariant(): CodesmithSidebarVariant {
  return readCachedSidebarVariant(orgSlugFromPath(window.location.pathname));
}

/**
 * The same open-pane footprint the live budget counts (see Codesmith). The
 * route's org isn't parsed yet when skeletons render, so the flag comes
 * from the org-less last-resolved cache.
 */
function workspacePaneSkeletonFootprint(): number {
  if (!readCachedWorkspacePaneEnabled(null)) return 0;
  const paneId = workspacePaneIdFromQuery(
    new URLSearchParams(window.location.search).get(
      WORKSPACE_PANE_QUERY_PARAM,
    ) ?? undefined,
  );
  return paneId !== null ? MIN_WORKSPACE_PANE_WIDTH : 0;
}

/**
 * The footprint the real session sidebar will restore: its persisted width,
 * hidden when the user collapsed it or the viewport can't fit it alongside
 * the chat pane's minimum.
 */
export function readLeftSidebarSkeletonLayout(): LeftSidebarSkeletonLayout {
  const variant = readSkeletonSidebarVariant();
  const config = sidebarWidthConfig(variant);
  const storedWidth = readStoredLayoutValue(
    config.storageKey,
    config.defaultWidth,
  );
  const width = clampSidebarWidth(storedWidth, config);
  const collapsed = readStoredLayoutValue(SESSION_SIDEBAR_COLLAPSED_KEY, false);
  const visible =
    !collapsed &&
    canFitLeftSidebar({
      containerWidth: estimateContainerWidth(),
      leftSidebarWidth: width,
      workspacePaneWidth: workspacePaneSkeletonFootprint(),
    });
  return { visible, width, variant };
}

/**
 * Width the right details panel will occupy, or null when it stays closed.
 * Best effort: whether the panel actually opens also depends on the session
 * having content to show (see `sessionHasRightSidebarContent`), which is
 * unknowable while the session is still loading — but nearly every session
 * being loaded has a conversation, so the preference is the dominant signal.
 */
export function readRightSidebarSkeletonWidth(): number | null {
  // Mobile renders the panel as a full-screen overlay that costs no width
  // (same cutoff as Codesmith's `isMobileViewport` media query).
  if (window.innerWidth < 768) return null;

  const width = Math.max(
    MIN_RIGHT_SIDEBAR_WIDTH,
    readStoredLayoutValue(RIGHT_SIDEBAR_WIDTH_KEY, DEFAULT_RIGHT_SIDEBAR_WIDTH),
  );
  const left = readLeftSidebarSkeletonLayout();
  const hasSpace = canFitRightSidebar({
    containerWidth: estimateContainerWidth(),
    openLeftSidebarWidth: left.visible ? left.width : 0,
    rightSidebarWidth: width,
  });
  // Mirrors useSpaceAwareRightSidebarPreference: with room the stored
  // preference decides; without room only the tab's manual narrow-open
  // override (sessionStorage, so it survives a reload) keeps the panel open.
  const open = hasSpace
    ? readStoredLayoutValue(RIGHT_SIDEBAR_OPEN_PREFERENCE_KEY, false)
    : readStoredValue(
        window.sessionStorage,
        RIGHT_SIDEBAR_NARROW_OPEN_OVERRIDE_KEY,
        false,
      );
  return open ? width : null;
}
